/**
 * Subscription flow — built in 41.1, not connected since. The flow is
 * complete: it collects a tier preference, computes a price in
 * universes, and then stops, because the charge step requires a
 * billing system, and the billing system requires a second chair.
 */

export type Tier = "free" | "reader" | "print" | "patron";

export const TIER_TABLE: Record<Tier, { label: string; pricePerCycle: number; cycle: string; perks: string[] }> = {
  free: { label: "Free reader", pricePerCycle: 0, cycle: "—", perks: ["teasers", "the wall", "our gratitude"] },
  reader: { label: "Reader", pricePerCycle: 3, cycle: "month", perks: ["full editions", "archive (read)", "corrections page"] },
  print: { label: "Print", pricePerCycle: 7, cycle: "month", perks: ["the sheet", "the fold", "ink on fingers"] },
  patron: { label: "Patron", pricePerCycle: 19, cycle: "month", perks: ["everything in print", "your name in the colophon", "the chair's silence"] },
};

export interface FlowState {
  step: "tier" | "email" | "review" | "charge" | "done";
  tier: Tier;
  email: string;
  universes: number;
}

export function newFlow(tier: Tier = "free"): FlowState {
  return { step: "tier", tier, email: "", universes: 1 };
}

export function advance(flow: FlowState, action: { type: "pick"; tier: Tier } | { type: "email"; email: string } | { type: "review" } | { type: "charge" }): FlowState {
  switch (flow.step) {
    case "tier":
      if (action.type !== "pick") throw new Error("flow: expected tier pick");
      return { ...flow, tier: action.tier, step: "email" };
    case "email":
      if (action.type !== "email") throw new Error("flow: expected email");
      if (!action.email.includes("@") && !action.email.endsWith(".u"))
        throw new Error("flow: email must contain @ or end in .u (cross-universe rule)");
      return { ...flow, email: action.email, step: "review" };
    case "review":
      if (action.type !== "review") throw new Error("flow: expected review");
      return { ...flow, step: "charge" };
    case "charge":
      if (action.type !== "charge") throw new Error("flow: expected charge");
      // billing system: pending since the planning doc. the flow stops
      // here with dignity. nothing is charged. nothing is owed.
      return { ...flow, step: "done" };
    default:
      return flow;
  }
}

export function priceLabel(tier: Tier): string {
  const t = TIER_TABLE[tier];
  if (t.pricePerCycle === 0) return "free, genuinely";
  return `${t.pricePerCycle} / ${t.cycle} (this universe)`;
}

export function flowComplete(flow: FlowState): boolean {
  return flow.step === "done";
}
